import { avatarUrl } from "@repo/app-shell"
import { api, authClient } from "@repo/db"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/shad"
import { Avatar, AvatarFallback, AvatarImage } from "@repo/shad/ui/avatar"
import { Button } from "@repo/shad/ui/button"
import { Input } from "@repo/shad/ui/input"
import { Label } from "@repo/shad/ui/label"
import { AuthLayout } from "@repo/ui"
import { IconArrowRight, IconCamera, IconLoader2 } from "@tabler/icons-react"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useAction } from "convex/react"
import { useRef, useState } from "react"
import { toast } from "sonner"

import { useConvex } from "@/hooks/use-convex"
import { convexClient, queryWithTimeout } from "@/lib/convex"

export const Route = createFileRoute("/complete-profile")({
  beforeLoad: async () => {
    const user = await queryWithTimeout(convexClient.query(api.auth.getCurrentUser))
    if (!user) throw redirect({ to: "/login", search: { redirect: undefined } })
    if (user.name && user.email) throw redirect({ to: "/", search: { redirect: undefined } })
  },
  component: CompleteProfile,
})

function useProfileForm() {
  const { data: currentUser } = useConvex(api.auth.getCurrentUser, {})
  const getAvatarUploadUrl = useAction(api.users.avatarActions.getAvatarUploadUrl)
  const confirmAvatarUpload = useAction(api.users.avatarActions.confirmAvatarUpload)
  const [name, setName] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const displayName = name ?? currentUser?.name ?? ""
  const displayEmail = email ?? currentUser?.email ?? ""
  const emailRequired = !currentUser?.email
  const displayAvatar = avatarPreview ?? currentUser?.image ?? undefined
  const initials = displayName
    .split(" ")
    .map((n: string) => n.charAt(0))
    .join("")
    .toUpperCase()

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file")
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB")
      return
    }

    setAvatarFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setAvatarPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleSave = async () => {
    const finalName = displayName.trim()
    const finalEmail = displayEmail.trim()
    if (!finalName) {
      toast.error("Please enter your name")
      return
    }
    if (emailRequired && !finalEmail) {
      toast.error("Please enter your email")
      return
    }

    setSaving(true)
    try {
      const updates: Record<string, string | undefined> = { name: finalName }

      if (avatarFile) {
        const { uploadUrl, imageId } = await getAvatarUploadUrl({
          fileName: avatarFile.name,
          fileSize: avatarFile.size,
          mimeType: avatarFile.type,
        })

        const formData = new FormData()
        formData.append("file", avatarFile)
        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          body: formData,
        })
        if (!uploadResponse.ok) throw new Error("Failed to upload image")

        const { avatarUrl } = await confirmAvatarUpload({ imageId })
        updates.image = avatarUrl
      }

      await authClient.updateUser(updates)
      if (finalEmail && finalEmail !== currentUser?.email) {
        await authClient.changeEmail({ newEmail: finalEmail })
      }
    } catch {
      toast.error("Failed to save profile")
      setSaving(false)
      throw new Error("save failed")
    } finally {
      setSaving(false)
    }
  }

  return {
    currentUser,
    displayName,
    displayEmail,
    emailRequired,
    displayAvatar,
    initials,
    saving,
    fileInputRef,
    setName,
    setEmail,
    handleFileSelect,
    handleSave,
  }
}

function CompleteProfile() {
  const navigate = useNavigate()
  const {
    displayName,
    displayEmail,
    emailRequired,
    displayAvatar,
    initials,
    saving,
    fileInputRef,
    setName,
    setEmail,
    handleFileSelect,
    handleSave,
  } = useProfileForm()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await handleSave()
      void navigate({ to: "/", search: { redirect: undefined } })
    } catch {
      // error already toasted
    }
  }

  return (
    <div className="dark">
      <AuthLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="font-display text-xl">Complete your profile</CardTitle>
            <CardDescription className="text-sm mt-2">
              Tell us your name{emailRequired ? " and email" : ""} to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-3">
                <div className="group relative">
                  <Avatar className="size-20 border-2 border-border shadow-md">
                    <AvatarImage
                      src={avatarUrl(displayAvatar, 80)}
                      alt="Profile photo"
                      className="object-cover"
                    />
                    <AvatarFallback className="text-xl font-semibold bg-muted text-muted-foreground">
                      {initials || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 transition-all group-hover:bg-black/40 cursor-pointer"
                  >
                    <IconCamera className="size-5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs text-muted-foreground"
                >
                  <IconCamera className="size-3.5" />
                  Add photo
                </Button>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="profile-name">Name</Label>
                <Input
                  id="profile-name"
                  value={displayName}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  autoFocus
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={displayEmail}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="youremail@example.com"
                  required={emailRequired}
                  disabled={!emailRequired}
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full"
                disabled={!displayName.trim() || (emailRequired && !displayEmail.trim()) || saving}
              >
                {saving ? (
                  <>
                    <IconLoader2 className="size-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <IconArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </AuthLayout>
    </div>
  )
}
